import React, { useState } from 'react';
import { List, LayoutGrid, Calendar as CalendarIcon } from 'lucide-react';
import { User } from '../../types';
import TaskList from './TaskList';
import KanbanBoard from './KanbanBoard';
import CalendarView from './CalendarView';

type TaskViewMode = 'list' | 'kanban' | 'calendar';

interface TaskViewProps {
  currentUser: User;
  onSelectTask: (taskId: string) => void;
  onBack: () => void;
}

const TaskView: React.FC<TaskViewProps> = ({ currentUser, onSelectTask, onBack }) => {
  // Load saved view mode from localStorage, default to 'list'
  const [viewMode, setViewMode] = useState<TaskViewMode>(() => {
    const saved = localStorage.getItem('taskViewMode');
    return (saved as TaskViewMode) || 'list';
  });

  // Save view mode to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem('taskViewMode', viewMode);
  }, [viewMode]);

  return (
    <div className="h-full flex flex-col">
      {/* View Switcher Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">All Tasks</h1>
          
          {/* View Mode Switcher */}
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all ${
                viewMode === 'list'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4" />
              List View
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all ${
                viewMode === 'kanban'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
              Kanban Board
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all ${
                viewMode === 'calendar'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <CalendarIcon className="w-4 h-4" />
              Calendar View
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'list' && (
          <TaskList currentUser={currentUser} onSelectTask={onSelectTask} onBack={onBack} />
        )}
        {viewMode === 'kanban' && (
          <KanbanBoard currentUser={currentUser} onTaskClick={onSelectTask} />
        )}
        {viewMode === 'calendar' && (
          <CalendarView currentUser={currentUser} onTaskClick={onSelectTask} />
        )}
      </div>
    </div>
  );
};

export default TaskView;
